import { useState, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { ChevronRight, ChevronLeft, CircleSlash, Search, X } from 'lucide-react'
import { useQuestionnaireStore, useCaseStore } from '../../stores'
import { useQuestionnaireTranslation } from '../../i18n/questionnaires/useQuestionnaireTranslation'
import { doctorMatchesCategory } from '../../config/specialties'
import { getSpecialtyIcon } from '../../utils/specialtyIcons'
import { isPatientSelectable, selectableFocusAreas } from '../../utils/specialtyResolver'
import { useI18n } from '../../i18n'
import BottomSheet from '../../components/BottomSheet'
import { officeSpecialtyName } from '../../utils/directoryOffice'
import { savePreferredOffice } from '../../utils/preferredOffice'
import { OrellaDialogue } from '../onboarding/onboardingPrimitives' // s110 b77: the Orella dialogue template
import { matchText } from '../../utils/startCaseSearch'
import { clinicianName } from '../../utils/clinicianName.js'

/**
 * StartCaseSheet (v230.6.0)
 *
 * Bottom-sheet launched from "Start a case" on an MD profile. Scopes the new
 * case to the doctor's specialty (an ENT can only spawn ENT-area cases), walks
 * the patient through area -> primary symptom (same data as the normal flow),
 * then routes to the shared case-start point (/case/emergency) with the MD
 * deep-linked via the existing `orella-preferred-doctor` handoff that
 * ShareScreen consumes. If a case is already in progress, it first asks whether
 * this is the current issue or a new one.
 *
 * Deliberately reuses the exact routing FocusAreas uses, so the case is created
 * by Emergency.jsx on proceed — no new case-creation path.
 *
 * ★ s106 (TestFlight b72, Dr. Rami: "Create an option to send a case - they can start the case workflow
 *   and have this provider saved as the doctor selection when they get to the share hub. Needs to work
 *   for the docs not current subscribed to orella."; mockup "Orella Doctors Tab Send", approved
 *   2026-10-16): `office` is a directory doctor (not on Orella) instead of `doctor`. Same steps. The
 *   areas are the ones the office's directory specialty covers (utils/directoryOffice), with "Show all
 *   areas" (directory specialties are broad; primary care and unknown ones show every area). The office
 *   is saved on the phone (utils/preferredOffice), NOT as 'orella-preferred-doctor', and the share hub
 *   offers it when the Case Report is ready.
 */

// Statuses where the case has been handed to a doctor or is terminal — NOT a
// "current draft" the patient is still building. Anything else is resumable.
const HANDED_OR_DONE = [
  'completed', 'in_follow_up', 'in_observation', 'in_progress',
  'checked_in', 'scheduled', 'claimed', 'shared', 'archived',
]

// v237.14.1: a case stores `specialty`/`focusArea` as objects ({id,name}),
// alongside flat `specialtyName`/`focusAreaName` strings. Coerce to the display
// string so labels never render "[object Object]".
function nameOf(v) {
  if (!v) return ''
  if (typeof v === 'string') return v
  return v.name || v.label || v.title || ''
}

function buildPreferredDoctor(doctor) {
  return {
    id: doctor?.id,
    first_name: doctor?.first_name,
    last_name: doctor?.last_name,
    specialty: ((doctor?.specialties && doctor.specialties[0]) || doctor?.specialty || ''),
    practice_name: doctor?.practice_name,
    practice_address: doctor?.practice_address,
    practice_city: doctor?.practice_city,
    practice_state: doctor?.practice_state,
    practice_zip: doctor?.practice_zip,
    practice_phone: doctor?.practice_phone,
    phone: doctor?.phone,
    board_certified: doctor?.board_certified,
    profile_photo: doctor?.profile_photo,
    bio: doctor?.bio,
    // P1 round 4 (L2): the evidence ShareScreen's hasRealHours reads when it consumes the
    //   handoff — `bookable` from GET /doctors/<id>/full-profile, and next_available_date
    //   when a directory row carried one. Absent keys stay absent (never a default).
    ...(typeof doctor?.bookable === 'boolean' ? { bookable: doctor.bookable } : {}),
    ...(doctor?.next_available_date ? { next_available_date: doctor.next_available_date } : {}),
  }
}

export default function StartCaseSheet({ doctor = null, office = null, onClose }) {
  const navigate = useNavigate()
  const { t } = useI18n()
  const { tfa } = useQuestionnaireTranslation()
  const { specialties, getSpecialty } = useQuestionnaireStore()
  const { cases, setAndSaveCase } = useCaseStore()

  const mdSpecialty = office
    ? (officeSpecialtyName(office.specialty) || 'General Medicine')
    : ((doctor?.specialties && doctor.specialties[0]) || doctor?.specialty || '')
  const [showAllAreas, setShowAllAreas] = useState(false)

  // Areas valid for this MD's specialty (patient-selectable + scope match).
  const allAreas = useMemo(() => (specialties || []).filter((s) => isPatientSelectable(s)), [specialties])
  const matchedAreas = useMemo(
    () => allAreas.filter((s) => doctorMatchesCategory(mdSpecialty, s.id)),
    [allAreas, mdSpecialty]
  )
  // An office whose specialty matches no area (or "Show all areas") offers every area.
  const areas = office && (showAllAreas || !matchedAreas.length) ? allAreas : matchedAreas
  const canShowAll = !!office && !showAllAreas && matchedAreas.length > 0 && matchedAreas.length < allAreas.length

  // Most-recent resumable (still-being-built) case, if any.
  const currentCase = useMemo(() => {
    const resumable = (cases || []).filter(
      (c) => !HANDED_OR_DONE.includes(String(c.status || '').toLowerCase())
    )
    if (!resumable.length) return null
    return resumable
      .slice()
      .sort((a, b) => new Date(b.updated_at || b.created_at || 0) - new Date(a.updated_at || a.created_at || 0))[0]
  }, [cases])

  const currentLabel = useMemo(() => {
    if (!currentCase) return ''
    const spec = currentCase.specialtyName || nameOf(currentCase.specialty)
    const focus = currentCase.focusAreaName || nameOf(currentCase.focusArea)
      || currentCase.focus_area || currentCase.chief_complaint || ''
    return [spec, focus].filter(Boolean).join(' \u203a ')
  }, [currentCase])

  const [step, setStep] = useState(currentCase ? 'warn' : 'area')
  const [areaSel, setAreaSel] = useState(null)
  const [learnId, setLearnId] = useState(null)
  const [escapeFrom, setStepFrom] = useState('area')
  // ★ s110 (TestFlight b77, Dr. Rami: "Replace the header text with the orella dialogue ux template. Add the search
  //   field for user to narrow search"; mockup "Orella Doctors Start Case" S1-S3, approved 2026-10-17).
  const [query, setQuery] = useState('')
  const [focusQuery, setFocusQuery] = useState('')
  const docLabel = office
    ? `${office.name || ''}${office.credential ? `, ${office.credential}` : ''}`.trim()
    : clinicianName(doctor || {})

  const areaName = (a) => t(`specialties.${a.id}`, { defaultValue: a.name }) || a.name
  // s100: retired focus areas are not offered for a new case (specialtyResolver owns the test).
  const focusesFor = (a) => selectableFocusAreas(getSpecialty(a.id))
  const coversBlurb = (a) =>
    focusesFor(a).slice(0, 5).map((f) => tfa(f.name)).filter(Boolean).join(', ')

  // ── Actions ────────────────────────────────────────────────────────────
  const deepLinkPreferredDoctor = (caseId = '') => {
    if (office) {
      // s106: the office waits for this case at the share hub; an earlier Orella pick must not win.
      try { sessionStorage.removeItem('orella-preferred-doctor') } catch { /* nothing to drop */ }
      savePreferredOffice(office, caseId)
      return
    }
    try {
      sessionStorage.setItem('orella-preferred-doctor', JSON.stringify(buildPreferredDoctor(doctor)))
    } catch { /* sessionStorage may be unavailable; Share simply won't pre-select */ }
  }

  const startNewCase = (focus, area = areaSel) => {
    deepLinkPreferredDoctor()
    navigate('/case/emergency', {
      state: {
        pendingCase: {
          specialtyId: area.id,
          specialtyName: area.name,
          focusAreaId: focus.id,
          focusAreaName: focus.name,
        },
      },
    })
    onClose?.()
  }

  const continueCurrentCase = () => {
    deepLinkPreferredDoctor(currentCase?.id || '')
    if (currentCase) setAndSaveCase(currentCase)
    navigate('/case/home')
    onClose?.()
  }

  const findAnotherDoctor = () => {
    navigate('/doctors')
    onClose?.()
  }

  // s110: what the search box finds — the areas by name, and the symptoms of those areas (a symptom starts the case).
  const q = query.trim()
  const areaHits = q ? areas.filter((a) => matchText(areaName(a), q) || matchText(a.name, q)) : []
  const symptomHits = q
    ? areas.flatMap((a) => focusesFor(a).filter((f) => matchText(tfa(f.name), q) || matchText(f.name, q)).map((f) => ({ f, a }))).slice(0, 12)
    : []
  const fq = focusQuery.trim()
  const focusList = areaSel ? focusesFor(areaSel).filter((f) => !fq || matchText(tfa(f.name), fq) || matchText(f.name, fq)) : []

  // A render function, not a component: a component defined here would remount on every keystroke and drop focus.
  const searchBox = (value, onChange, placeholder, testid) => (
    <label className="flex items-center gap-2 rounded-[14px] px-3.5 py-2.5 mb-3" style={{ border: value ? '1.5px solid #46A8F9' : '1px solid #DCE6EE', background: '#fff' }}>
      <Search size={16} style={{ color: value ? '#46A8F9' : '#5F7A8E' }} />
      <input value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} data-testid={testid}
             className="flex-1 min-w-0 bg-transparent outline-none text-[15px] font-medium" style={{ color: '#15354F' }} />
      {value && (
        <button type="button" onClick={() => onChange('')} aria-label={t('common.clear', 'Clear')} className="w-[22px] h-[22px] rounded-full grid place-items-center" style={{ background: '#E7EEF4', color: '#5F7A8E' }}>
          <X size={12} />
        </button>
      )}
    </label>
  )

  // ── Render helpers ─────────────────────────────────────────────────────
  const Back = ({ to }) => (
    <button onClick={() => setStep(to)} className="flex items-center gap-1 text-[13px] font-bold py-1.5" style={{ color: '#5F7A8E' }}>
      <ChevronLeft size={16} /> {t('startCase.back', { defaultValue: 'Back' })}
    </button>
  )

  const EscapeLink = ({ from }) => (
    <button onClick={() => { setStep('escape'); setStepFrom(from) }} className="w-full text-center text-[12.5px] font-bold pt-3 pb-1" style={{ color: '#46A8F9' }}>
      {t('startCase.notListed', { defaultValue: "My concern isn't listed here \u2192" })}
    </button>
  )

  return (
    <BottomSheet open onClose={onClose} maxHeight="88dvh">
      {/* v237.14.1: render through the shared BottomSheet so the sheet portals
          to document.body — out of the transformed PageWrapper ancestor that
          previously clipped the lower button (the "new issue" path was unreachable). */}

      {/* MD row */}
      <div className="flex items-center gap-2.5 px-5 pb-3 flex-shrink-0" style={{ borderBottom: '1px solid #E7EEF4' }}>
        <div className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 font-extrabold text-[13px]" style={{ background: '#fff', border: '1px solid #A7D9FC', color: '#46A8F9' }}>
          {office ? officeInitials(office.name) : (`${(doctor?.first_name || '')[0] || ''}${(doctor?.last_name || '')[0] || ''}`.toUpperCase() || 'DR')}
        </div>
        <div className="min-w-0">
          <p className="text-[14px] font-extrabold leading-tight" style={{ color: '#15354F' }}>
            {office ? `${office.name || ''}${office.credential ? `, ${office.credential}` : ''}` : clinicianName(doctor || {})}
          </p>
          <p className="text-[11.5px] font-semibold" style={{ color: '#46A8F9' }}>
            {office ? [office.specialty, t('startCase.notOnOrella', 'Not on Orella')].filter(Boolean).join(' \u00b7 ') : mdSpecialty}
          </p>
        </div>
      </div>

      {/* Steps — own scroll area, so the lower button is always reachable on
          short / iOS viewports. PROD-1176: the BottomSheet panel pays the bottom
          safe-area inset (.bottom-actions), so this keeps only its own 1.25rem;
          paying env() here too would add the inset twice. */}
      <div className="overflow-y-auto px-5 pt-3.5" style={{ paddingBottom: '1.25rem', overscrollBehavior: 'contain' }}>

        {/* WARN — in-progress case present */}
        {step === 'warn' && (
          <div>
            <p className="text-[11px] font-extrabold tracking-wide uppercase mb-1" style={{ color: '#5F7A8E' }}>{t('startCase.beforeWeStart', { defaultValue: 'Before we start' })}</p>
            <p className="text-[18px] font-extrabold leading-snug mb-3.5" style={{ color: '#15354F' }}>{t('startCase.warnQ', { defaultValue: 'Is this about your current case, or something new?' })}</p>
            {currentLabel && (
              <p className="text-[13px] font-medium leading-snug mb-4" style={{ color: '#5F7A8E' }}>
                {t('startCase.inProgress', { defaultValue: 'You have a case in progress:' })} <span className="font-extrabold" style={{ color: '#15354F' }}>{currentLabel}</span>.
              </p>
            )}
            <button onClick={continueCurrentCase} className="w-full rounded-[14px] py-3.5 text-[14.5px] font-extrabold mb-2.5" style={{ background: '#fff', border: '1px solid #E7EEF4', color: '#15354F' }}>
              {t('startCase.continueCurrent', { defaultValue: 'Continue my current case' })}
            </button>
            <button onClick={() => setStep('area')} className="w-full rounded-[14px] py-3.5 text-[14.5px] font-extrabold" style={{ background: '#15354F', color: '#fff' }}>
              {t('startCase.newIssue', { defaultValue: "It's a new issue to discuss" })}
            </button>
          </div>
        )}

        {/* AREA picker */}
        {step === 'area' && (
          <div>
            {currentCase && <Back to="warn" />}
            <div data-testid={office ? 'start-case-office-note' : 'start-case-dialogue'}>
              <OrellaDialogue>
                {office
                  ? t('startCase.dialogueOffice', { defaultValue: "Let's build your case for {{doctor}}. What is it about? When your Case Report is ready, you can download it or fax it to the office.", doctor: docLabel })
                  : t('startCase.dialogueOrella', { defaultValue: "Let's build your case for {{doctor}}. What is it about?", doctor: docLabel })}
              </OrellaDialogue>
            </div>
            <p className="text-[11px] font-extrabold tracking-wide uppercase mb-2 mt-2" style={{ color: '#5F7A8E' }}>{t('startCase.step1', { defaultValue: 'New case \u00b7 Step 1 of 2' })}</p>
            {searchBox(query, setQuery, t('startCase.searchAreaOrSymptom', 'Search an area or a symptom'), "start-case-search")}
            {q ? (
              <div data-testid="start-case-results">
                {areaHits.length > 0 && <p className="text-[11px] font-extrabold tracking-wide uppercase mb-2 mt-1" style={{ color: '#5F7A8E' }}>{t('startCase.areasHeading', 'Areas')}</p>}
                {areaHits.map((a) => (
                  <button key={a.id} onClick={() => { setAreaSel(a); setFocusQuery(''); setStep('focus') }} className="flex items-center gap-3 w-full rounded-2xl px-3.5 py-3 mb-2 text-left active:scale-[0.99] transition-transform" style={{ border: '1px solid #E7EEF4', background: '#fff' }}>
                    <img src={getSpecialtyIcon(a)} alt="" className="w-[30px] h-[30px] object-contain flex-shrink-0" />
                    <span className="min-w-0 flex-1">
                      <span className="block text-[15px] font-bold" style={{ color: '#15354F' }}>{areaName(a)}</span>
                      {coversBlurb(a) && <span className="block text-[12px] font-semibold truncate" style={{ color: '#5F7A8E' }}>{coversBlurb(a)}</span>}
                    </span>
                    <ChevronRight size={18} className="ml-auto flex-shrink-0" style={{ color: '#C8D6E2' }} />
                  </button>
                ))}
                {symptomHits.length > 0 && <p className="text-[11px] font-extrabold tracking-wide uppercase mb-2 mt-2" style={{ color: '#5F7A8E' }}>{t('startCase.symptomsHeading', 'Symptoms \u00b7 start right there')}</p>}
                {symptomHits.map(({ f, a }) => (
                  <button key={`${a.id}:${f.id}`} onClick={() => { setAreaSel(a); startNewCase(f, a) }} className="flex items-center gap-3 w-full rounded-2xl px-3.5 py-3 mb-2 text-left active:scale-[0.99] transition-transform" style={{ border: '1px solid #E7EEF4', background: '#fff' }}>
                    <span className="min-w-0 flex-1">
                      <span className="block text-[15px] font-bold" style={{ color: '#15354F' }}>{tfa(f.name)}</span>
                      <span className="block text-[12px] font-bold" style={{ color: '#46A8F9' }}>{areaName(a)}</span>
                    </span>
                    <ChevronRight size={18} className="flex-shrink-0" style={{ color: '#C8D6E2' }} />
                  </button>
                ))}
                {areaHits.length === 0 && symptomHits.length === 0 && (
                  <p className="text-[13px] font-medium px-1 py-2" style={{ color: '#5F7A8E' }}>{t('startCase.noMatch', { defaultValue: 'Nothing matches "{{q}}".', q })}</p>
                )}
              </div>
            ) : areas.map((a) => (
              <div key={a.id}>
                <button onClick={() => { setAreaSel(a); setFocusQuery(''); setStep('focus') }} className="flex items-center gap-3 w-full rounded-2xl px-3.5 py-3 mb-2 text-left active:scale-[0.99] transition-transform" style={{ border: '1px solid #E7EEF4', background: '#fff' }}>
                  <img src={getSpecialtyIcon(a)} alt="" className="w-[30px] h-[30px] object-contain flex-shrink-0" />
                  <span className="min-w-0">
                    <span className="block text-[15px] font-bold" style={{ color: '#15354F' }}>{areaName(a)}</span>
                    <span role="button" onClick={(e) => { e.stopPropagation(); setLearnId(learnId === a.id ? null : a.id) }} className="text-[11.5px] font-bold" style={{ color: '#46A8F9' }}>
                      {t('startCase.learnMore', { defaultValue: 'Learn more' })}
                    </span>
                  </span>
                  <ChevronRight size={18} className="ml-auto flex-shrink-0" style={{ color: '#C8D6E2' }} />
                </button>
                {learnId === a.id && coversBlurb(a) && (
                  <p className="text-[12px] leading-snug px-3.5 -mt-1 mb-2.5" style={{ color: '#5F7A8E' }}>
                    <span className="font-bold" style={{ color: '#15354F' }}>{t('startCase.covers', { defaultValue: 'Covers:' })}</span> {coversBlurb(a)}
                  </p>
                )}
              </div>
            ))}
            {office ? (canShowAll && (
              <button onClick={() => { setShowAllAreas(true); setQuery('') }} data-cta="start-case-all-areas" className="w-full text-center text-[12.5px] font-bold pt-3 pb-1" style={{ color: '#46A8F9' }}>
                {t('startCase.showAllAreas', 'Something else? Show all areas')}
              </button>
            )) : <EscapeLink from="area" />}
          </div>
        )}

        {/* FOCUS picker */}
        {step === 'focus' && areaSel && (
          <div>
            <Back to="area" />
            <div key={areaSel.id} data-testid="start-case-focus-dialogue">
              <OrellaDialogue>
                {t('startCase.dialogueFocus', { defaultValue: "{{area}} it is. What's bothering you most? I'll ask a few questions next and put it all in your Case Report.", area: areaName(areaSel) })}
              </OrellaDialogue>
            </div>
            <p className="text-[11px] font-extrabold tracking-wide uppercase mb-2 mt-2" style={{ color: '#5F7A8E' }}>{t('startCase.step2', { defaultValue: 'New case \u00b7 Step 2 of 2' })} {'\u00b7'} {areaName(areaSel)}</p>
            {searchBox(focusQuery, setFocusQuery, t('startCase.searchSymptom', 'Search a symptom'), "start-case-focus-search")}
            {fq && focusList.length === 0 && (
              <p className="text-[13px] font-medium px-1 py-2" style={{ color: '#5F7A8E' }}>{t('startCase.noMatch', { defaultValue: 'Nothing matches "{{q}}".', q: fq })}</p>
            )}
            {focusList.map((f) => (
              <button key={f.id} onClick={() => startNewCase(f)} className="flex items-center gap-3 w-full rounded-2xl px-3.5 py-3 mb-2 text-left active:scale-[0.99] transition-transform" style={{ border: '1px solid #E7EEF4', background: '#fff' }}>
                <span className="block text-[15px] font-bold flex-1 min-w-0" style={{ color: '#15354F' }}>{tfa(f.name)}</span>
                <ChevronRight size={18} className="flex-shrink-0" style={{ color: '#C8D6E2' }} />
              </button>
            ))}
            <EscapeLink from="focus" />
          </div>
        )}

        {/* ESCAPE — outside scope */}
        {step === 'escape' && (
          <div>
            <Back to={escapeFrom} />
            <div className="text-center pt-5">
              <CircleSlash size={34} className="mx-auto mb-3.5" style={{ color: '#9FB3C4' }} />
              <p className="text-[16px] font-extrabold leading-snug mb-5 px-2" style={{ color: '#15354F' }}>
                {t('startCase.outOfScope', { defaultValue: "This is outside the scope of this doctor's practice. Select another doctor for this problem." })}
              </p>
              <button onClick={findAnotherDoctor} className="w-full rounded-[14px] py-3.5 text-[14.5px] font-extrabold mb-2.5" style={{ background: '#15354F', color: '#fff' }}>
                {t('startCase.findAnother', { defaultValue: 'Find another doctor' })}
              </button>
              <button onClick={() => setStep(escapeFrom)} className="w-full rounded-[14px] py-3.5 text-[14.5px] font-extrabold" style={{ background: '#fff', border: '1px solid #E7EEF4', color: '#15354F' }}>
                {t('startCase.goBack', { defaultValue: 'Go back' })}
              </button>
            </div>
          </div>
        )}
      </div>
    </BottomSheet>
  )
}

function officeInitials(name) {
  const parts = String(name || '').replace(/^dr\.?\s+/i, '').split(/\s+/).filter(Boolean)
  return (((parts[0] || '')[0] || '') + ((parts[parts.length - 1] || '')[0] || '')).toUpperCase() || 'DR'
}
